
import { useState, useRef, useEffect } from 'react'
import { Modal, Button, Input } from '../ui'
import { askGemini } from '../../lib/gemini'
import { Send, User, Bot, HelpCircle, Loader2 } from 'lucide-react'

export const RefereeAssistantModal = ({ isOpen, onClose, tournamentSport }) => {
    const [messages, setMessages] = useState([
        { role: 'model', text: `Hoi! Ik ben de VAR Assistent. Heb je een vraag over de regels bij ${tournamentSport || 'dit toernooi'}?` }
    ])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const scrollRef = useRef(null)

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
    }, [messages])

    const handleSend = async (e) => {
        e.preventDefault()
        if (!input.trim()) return

        const userMsg = { role: 'user', text: input }
        setMessages(prev => [...prev, userMsg])
        setInput('')
        setLoading(true)

        try {
            // Build history for Gemini
            const history = messages.map(m => ({
                role: m.role,
                parts: [{ text: m.text }]
            }))

            const systemPrompt = `
            Je bent een expert scheidsrechter assistent (VAR) voor een ${tournamentSport || 'sport'} toernooi.
            Je helpt de scheidsrechter op het veld met korte, duidelijke antwoorden over spelregels.
            Houd rekening met amateur niveau (wat vaak soepeler is, tenzij anders gevraagd).
            Antwoord direct en to-the-point zodat het spel snel door kan.
            `

            const prompt = `${systemPrompt}\n\nVraag: ${input}`
            const responseText = await askGemini(prompt, history) // Note: history support depends on implementation, passing prompt with context is safer for simple helper

            setMessages(prev => [...prev, { role: 'model', text: responseText }])
        } catch (err) {
            setMessages(prev => [...prev, { role: 'model', text: "Sorry, ik kon de regel niet vinden." }])
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="VAR & Regel Hulp" size="md">
            <div className="flex flex-col h-[500px]">
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 rounded-xl mb-4 border border-gray-100">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-blue-600' : 'bg-orange-500'}`}>
                                    {msg.role === 'user' ? <User size={16} className="text-white" /> : <Bot size={16} className="text-white" />}
                                </div>
                                <div className={`p-3 rounded-2xl text-sm ${msg.role === 'user'
                                        ? 'bg-blue-600 text-white rounded-tr-none'
                                        : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none shadow-sm'
                                    }`}>
                                    {msg.text}
                                </div>
                            </div>
                        </div>
                    ))}
                    {loading && (
                        <div className="flex justify-start">
                            <div className="flex gap-2 max-w-[85%]">
                                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                                    <Bot size={16} className="text-white" />
                                </div>
                                <div className="bg-white border border-gray-200 p-3 rounded-2xl rounded-tl-none shadow-sm flex items-center">
                                    <Loader2 size={16} className="animate-spin text-gray-400" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSend} className="flex gap-2">
                    <Input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        placeholder="Typ je vraag (bijv. 'Is hands altijd geel?')"
                        className="flex-1"
                        autoFocus
                    />
                    <Button type="submit" disabled={loading || !input.trim()} className="bg-orange-500 hover:bg-orange-600 text-white">
                        <Send size={20} />
                    </Button>
                </form>

                <div className="mt-2 text-center">
                    <p className="text-xs text-gray-400">Advies is gebaseerd op algemene regels. De scheidsrechter beslist altijd.</p>
                </div>
            </div>
        </Modal>
    )
}
